import {
  init as amplitudeInit,
  track as amplitudeTrackEvent,
  getSessionId,
  setUserId,
  setDeviceId
} from '@amplitude/analytics-browser';
import endpoints from '../../ajio-util/endpoints';
import {
  getAffiliateFeatureConfig,
  getAffiliateAttributionWindow,
  getYoutubeAffiliateAttributionWindow,
  getCreatorProgramConfig,
  getEnableWebVitals
} from '../../../refresh/selectors/featureFlagsSelector';
import { getAffiliateCookie, setAffiliateCookie } from '../../../utils/affiliateUtils';
import {
  readCohortValueCookie,
  readCookie,
  createCookie,
  eraseCookie
} from '../../../components/common/Cookie';
import { signInCheck } from '../../../components/common/CheckUserStatus';
import { getLoyaltyTier } from '../../../refresh/selectors/centralLoyalty';

const CryptoJS = require('crypto-js');

const amplitudeApiKey = endpoints.getProperties('amplitude_api_key');
// Static config id (not per-user), same source as segment.js's identify traits.
const cleverTapId = endpoints.getProperties('cleverTapID');
const AMPLITUDE_SESSION_TIMEOUT = 30 * 60 * 1000; // 30 min
const SESSION_UTM_KEY = 'amplitude_session_utm';

let isInitialized = false;

const isBrowser = () =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof document.cookie === 'string';

const AFFILIATE_KEYS = [
    'attributed_affiliate_name',
    'attributed_affiliate_id',
    'attributed_affiliate_campaign_id',
    'attributed_affiliate_click_id',
    'attributed_affiliate_sub_id',
    'attributed_affiliate_additional_publisher_info',
    'attributed_affiliate_campaign_name',
    'attributed_affiliate_campaign_medium'
  ];

if(isBrowser()){
  const isAffiliateFeatureEnabled = getAffiliateFeatureConfig(window?.__PRELOADED_STATE__);
  const affiliateAttributionWindow = getAffiliateAttributionWindow(window?.__PRELOADED_STATE__);
  const youtubeAffiliateAttributionWindow = getYoutubeAffiliateAttributionWindow(window?.__PRELOADED_STATE__);
  const isCreatorFeatureEnabled = getCreatorProgramConfig(window?.__PRELOADED_STATE__);
  console.log(
    'Affiliate logs: [amplitude.js][module-init] affiliateFeatureConfig:',
    isAffiliateFeatureEnabled
  );
  console.log(
    'Affiliate logs: [amplitude.js][module-init] creatorFeatureConfig:',
    isCreatorFeatureEnabled
  );
  console.log(
    'Affiliate logs: [amplitude.js][module-init] attributionWindow(hours):',
    affiliateAttributionWindow
  );
  const affiliateConfigForCookie = {
    ...(isAffiliateFeatureEnabled || {}),
    attributionWindow: affiliateAttributionWindow,
    youtubeAttributionWindow: youtubeAffiliateAttributionWindow
  };
  console.log(
    'Affiliate logs: [amplitude.js][module-init] calling setAffiliateCookie, creatorEnabled:',
    !!isCreatorFeatureEnabled?.masterFlag
  );
  if (isCreatorFeatureEnabled?.masterFlag) {
    setAffiliateCookie(affiliateConfigForCookie, isCreatorFeatureEnabled);
  } else {
    setAffiliateCookie(affiliateConfigForCookie, {});
  }
}

const parseUTMParams = () => {
  let search = window.location.search || '';
  search = search.startsWith('?') ? search.slice(1) : search;
  const params = {};
  search.split('&').forEach(item => {
    const [key, value] = item.split('=');
    if (key) params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
  });
  return params;
};

const NA = 'NA';

const ANDROID_BRANDS = {
  samsung: /SM-|GT-|Samsung|Galaxy/i,
  Xiaomi: /Redmi|POCO|Mi\s|Xiaomi/i,
  OnePlus: /OnePlus/i,
  Google: /Pixel/i,
  Realme: /Realme|RMX\d+/i,
  Oppo: /Oppo|CPH\d+/i,
  Vivo: /Vivo/i,
  Motorola: /Moto|Motorola/i,
  Nokia: /Nokia/i,
  Huawei: /Huawei|Honor/i,
  Lenovo: /Lenovo/i,
  Infinix: /Infinix/i,
  Tecno: /Tecno/i,
  iQOO: /IQOO/i,
};

const getDeviceProps = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const base = { app_version: NA, carrier: NA };

  if (/iPad/i.test(ua)) return { ...base, device_brand: 'Apple', device_manufacturer: 'Apple', device_model: 'iPad' };
  if (/iPod/i.test(ua)) return { ...base, device_brand: 'Apple', device_manufacturer: 'Apple', device_model: 'iPod' };
  if (/iPhone/i.test(ua)) return { ...base, device_brand: 'Apple', device_manufacturer: 'Apple', device_model: 'iPhone' };

  if (/Android/i.test(ua)) {
    const modelMatch = ua.match(/Android\s+[\d.]+;\s*([^;)]+?)(?:\s+Build\/|\)|;)/i);
    const model = modelMatch ? modelMatch[1].trim() : NA;
    const brand = Object.keys(ANDROID_BRANDS).find(b => ANDROID_BRANDS[b].test(ua)) || NA;
    return { ...base, device_brand: brand, device_manufacturer: brand, device_model: model };
  }

  return { ...base, device_brand: NA, device_manufacturer: NA, device_model: NA };
};

const REFERRAL_COUNT_COOKIE = 'successful_referrals';
// Long expiry: the count is dropped on logout, not by the cookie expiring.
const REFERRAL_COUNT_COOKIE_DAYS = 30;

export const setAmplitudeReferralProps = ({ successful_referrals: referralCount } = {}) => {
  // Only skip when there is no count to store. A truthiness check would drop 0,
  // which is a real count — and worse, would leave a previous non-zero cookie in
  // place for every later event.
  const hasCount = referralCount !== '' && referralCount !== undefined && referralCount !== null;
  if (!isBrowser() || !hasCount) return;
  createCookie(REFERRAL_COUNT_COOKIE, referralCount, REFERRAL_COUNT_COOKIE_DAYS);
};

/** Called on logout — the referral count must not outlive the session. */
export const clearAmplitudeReferralProps = () => {
  if (isBrowser()) eraseCookie(REFERRAL_COUNT_COOKIE);
};

const getReferralProps = () => {
  const referralCount = readCookie(REFERRAL_COUNT_COOKIE);
  return { successful_referrals: referralCount ? Number(referralCount) : NA };
};

const FIRST_VISIT_DATE_KEY = 'first_visit_date';

// Stored as YYYY-MM-DD. Anything else in the key (legacy/garbage value) is
// treated as absent and overwritten, so the property is always a real date.
const isValidFirstVisitDate = value =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(new Date(value).getTime());

const getFirstVisitDateProps = () => {
  // localStorage is a browser-only signal.
  if (!isBrowser()) return { first_visit_date: NA };

  let stored = null;
  try {
    stored = window.localStorage.getItem(FIRST_VISIT_DATE_KEY);
  } catch (e) {
    // localStorage can throw in private mode / when storage is blocked. Not
    // memoised: the read may start working later in the page's life.
    return { first_visit_date: NA };
  }

  if (isValidFirstVisitDate(stored)) return { first_visit_date: stored };

  const today = new Date().toISOString().split('T')[0];
  try {
    window.localStorage.setItem(FIRST_VISIT_DATE_KEY, today);
  } catch (e) {
    // write blocked (quota / private mode) — still report today's date
  }
  return { first_visit_date: today };
};

const VISITOR_TYPE_KEY = 'new_repeat_visitor';

// A visitor is "new" while their session stays active — i.e. every event keeps
// reporting "new" as long as the gap since the last event is under the timeout.
// The first 30-min-plus inactivity gap flips them to "repeat" permanently; from
// then on every event reports "repeat".
// Stored as "<lastActivityEpochMs>|<type>". Each call refreshes the timestamp
// so the activity window keeps sliding.
const SESSION_INACTIVITY_MS = 30 * 60 * 1000;

// Debounce the localStorage timestamp write: rapid-fire events would otherwise
// hit the store on every call. The write is deferred 10s and rescheduled on each
// call, so it lands once the burst settles. The timestamp is captured at flush
// time (not call time) to keep the activity window accurate.
const VISITOR_WRITE_DEBOUNCE_MS = 5 * 1000;
let visitorWriteTimer = null;

const debouncedWriteVisitorType = type => {
  if (visitorWriteTimer) {
    clearTimeout(visitorWriteTimer);
  }
  visitorWriteTimer = setTimeout(() => {
    visitorWriteTimer = null;
    try {
      window.localStorage.setItem(VISITOR_TYPE_KEY, `${Date.now()}|${type}`);
    } catch (e) {
      // write blocked — the computed type was already reported to the caller
    }
  }, VISITOR_WRITE_DEBOUNCE_MS);
};

export const getNewRepeatVisitor = () => {
  if (!isBrowser()) return 'repeat';

  const now = Date.now();

  let stored = null;
  try {
    stored = window.localStorage.getItem(VISITOR_TYPE_KEY);
  } catch (e) {
    return 'repeat';
  }

  const [tsPart, typePart] = (stored || '').split('|');
  const lastActivity = Number(tsPart);

  let type;
  if (!tsPart || Number.isNaN(lastActivity)) {
    // First ever event (or legacy value) — start of the "new" session.
    type = 'new';
  } else if (now - lastActivity > SESSION_INACTIVITY_MS) {
    // Inactivity gap crossed — flip to "repeat" for good.
    type = 'repeat';
  } else {
    // Still active — keep whatever we were.
    type = typePart === 'repeat' ? 'repeat' : 'new';
  }

  debouncedWriteVisitorType(type);

  return type;
};

const getAffiliateProps = () => {
  const affiliateData = getAffiliateCookie();
  console.log('Affiliate logs: [amplitude.js][getAffiliateProps] cookie:', affiliateData);
  // 🔥 ALWAYS reset affiliate traits (even if no affiliateData present)
  if (!affiliateData) {
    const affiliate = {};
    AFFILIATE_KEYS.forEach(key => {
      affiliate[key] = 'NA';
    });
    console.log(
      'Affiliate logs: [amplitude.js][getAffiliateProps] no cookie -> NA traits:',
      affiliate
    );
    return affiliate;
  }
  console.log(
    'Affiliate logs: [amplitude.js][getAffiliateProps] returning cookie data:',
    affiliateData
  );
  return affiliateData;
};

const getSubscriptionAndSavingsProps = () => {
  let state = null;
  if (typeof window !== 'undefined') {
    try {
      state = window.store?.getState?.() || window.__PRELOADED_STATE__ || null;
    } catch (e) {
      state = window.__PRELOADED_STATE__ || null;
    }
  }
  const membershipData = state?.membership?.membershipData || {};
  const detail = membershipData.detail || {};
  const userDetail = membershipData.userDetail || {};
  const savingBreakup = userDetail.savingBreakup || {};
  return {
    subscription_status: membershipData.status ?? NA,
    subscription_version: detail.identifier ?? NA,
    subscription_id: detail.productId ?? NA,
    subscripiton_number: NA,
    subscription_start_date: userDetail.boughtOn ?? NA,
    subscription_end_date: userDetail.validTill ?? NA,
    subscribed_orders_left: userDetail.orderLeft ?? NA,
    subscription_first_purchase_date: NA,
    total_account_savings: userDetail.totalSavings ?? NA,
    platform_account_savings: savingBreakup.platformFeeSaving ?? NA,
    delivery_account_savings: savingBreakup.deliveryFeeSaving ?? NA,
    membership_discount_account_savings: savingBreakup.membershipSaving ?? NA,
  };
};

// RONE points + SuperCash balances from the runtime cart wallet slice (state.internalWalletDetails).
const getInternalWalletBalances = () => {
  let state = null;
  if (typeof window !== 'undefined') {
    try {
      state = window.store?.getState?.() || window.__PRELOADED_STATE__ || null;
    } catch (e) {
      state = window.__PRELOADED_STATE__ || null;
    }
  }
  const internalWallets = state?.internalWalletDetails?.internalWallets || [];
  const roneWallet = internalWallets.find(w => w?.code === 'RELIANCE_ONE_WALLET');
  const superCashWallet = (
    internalWallets.find(w => w?.code === 'AJIO_CASH_WALLET')?.subWallets || []
  ).find(s => s?.code === 'AJIO_CASH_POINTS_WALLET');
  return {
    relianceOnePoints: roneWallet?.availableAmountPoints,
    superCash: superCashWallet?.availableAmount
  };
};

const SESSION_UTM_MAX_AGE = 30 * 60; // seconds

const setCookie = (name, value, maxAgeSeconds) => {
  try {
    const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch {}
};

const getCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const removeCookie = (name) => {
  try {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  } catch {}
};

const sha256Hex = message => (message ? CryptoJS.SHA256(message).toString(CryptoJS.enc.Hex) : '');

// Cookie-derived customer attributes, mirroring the identify traits in
// segment.js, so they ride on EVERY track event regardless of identify
// timing/identity. Conditionals match segment.js: absent values are omitted.
// NOTE: uses readCookie (raw, un-decoded) — the same reader segment.js uses —
// so values are byte-for-byte identical to the identify traits.
const getCustomerProps = () => {
  const isSignedIn = !!signInCheck();
  const isNew = (readCookie('customerType') || 'new').toLowerCase() === 'new';
  const storeName =
    (typeof window !== 'undefined' && window.__PRELOADED_STATE__?.metaData?.name) || '';
  const cohort = readCohortValueCookie('userCohortValues', storeName, 'cms');
  const pincode = readCookie('PC');
  const city = (readCookie('CT') || '').toLowerCase();
  const region = (readCookie('ZN') || '').toLowerCase();
  const relianceOneTier = getLoyaltyTier(window?.__PRELOADED_STATE__);
  const { relianceOnePoints, superCash } = getInternalWalletBalances();

  return {
    // Visitor recency comes from localStorage, not the customerType cookie:
    // 'new' only on the browser's first-ever visit, 'repeat' from then on,
    // signed in or not.
    new_repeat_visitor: getNewRepeatVisitor(),
    // new_repeat_customer_flag: isSignedIn ? isNew : true,
    new_repeat_customer_type: isSignedIn ? (isNew ? 'New' : 'Existing') : 'Guest',
    // cleverTap_Id: cleverTapId || '',
    ...(cohort && { customer_cohort: cohort }),
    ...(pincode && { pincode }),
    ...(city && { city }),
    ...(region && { region }),
    ...(relianceOneTier && { reliance_one_tier: relianceOneTier }),
    ...(Number.isFinite(relianceOnePoints) && { reliance_one_points_available: relianceOnePoints }),
    ...(Number.isFinite(superCash) && { supercash_available: superCash })
  };
};

// Hashed user identity attributes, mirroring segment.js's identify traits.
// SHA-256 is comparatively expensive to run on every event, so we memoise: the
// hashes are recomputed only when one of the underlying identity cookies
// actually changes (login/logout), otherwise the cached result is reused.
// let cachedHashedUserSignature = '';
// let cachedHashedUserProps = {};

// const getHashedUserProps = () => {
//   // readCookie returns the raw (still URL-encoded) value — identical to what
//   // segment.js hashes for its identify traits. Decoding here (e.g. via the local
//   // getCookie) would change the input and produce a different hash than identify.
//   const pk = readCookie('PK') || '';
//   const name = readCookie('un') || '';
//   const email = readCookie('U') || '';
//   const mobileNumber = readCookie('MN') || '';

//   const signature = `${pk}|${name}|${email}|${mobileNumber}`;
//   if (signature === cachedHashedUserSignature) return cachedHashedUserProps;

//   cachedHashedUserSignature = signature;
//   cachedHashedUserProps = {
//     ...(pk && { customer_pk: sha256Hex(pk) }),
//     ...(name && { name: sha256Hex(name.toLowerCase()) }),
//     ...(email && { email: sha256Hex(email.toLowerCase()) }),
//     ...(mobileNumber && { mobileNumber: sha256Hex(mobileNumber) })
//   };
//   return cachedHashedUserProps;
// };

// Stored in a cookie so the captured UTM survives across tabs within the
// Amplitude session window. Expiry is rolling: every successful read
// re-sets the cookie with a fresh 30-min max-age, so the entry only drops
// after a full 30 min of inactivity (no events read it).
const storeSessionUTM = (fields) => {
  setCookie(SESSION_UTM_KEY, JSON.stringify(fields), SESSION_UTM_MAX_AGE);
};

const readSessionUTM = () => {
  const raw = getCookie(SESSION_UTM_KEY);
  if (!raw) return null;
  try {
    const fields = JSON.parse(raw);
    // Rolling 30-min inactivity: any activity that reads the session UTM
    // extends the window. After 30 min with no reads, the cookie expires
    // and the next event falls back to NA.
    storeSessionUTM(fields);
    return fields;
  } catch {
    removeCookie(SESSION_UTM_KEY);
    return null;
  }
};

const NA_SESSION_FIELDS = {
  session_source: 'NA',
  session_medium: 'NA',
  session_campaign_id: 'NA',
  session_campaign_name: 'NA',
  session_campaign_ad_id: 'NA',
  session_campaign_ad_name: 'NA',
  session_campaign_adset_id: 'NA',
  session_campaign_adset_name: 'NA',
  session_pid: 'NA',
  session_offer_id: 'NA',
  session_click_id: 'NA'
};

// `allowUrlCapture` is true only on page-load init. Subsequent per-event calls
// must read from storage only — otherwise the URL bar (which still holds the
// entry UTM until the user navigates) would override the inactivity expiry on
// every event and the rolling timeout would never actually fire.
// On init, fresh UTMs in the URL take precedence and overwrite the stored cookie
// so a new campaign visit reattributes the session instead of being ignored.
const captureSessionUTM = (allowUrlCapture = false) => {
  if (allowUrlCapture) {
    const params = parseUTMParams();
    const hasUTM = !!(params.utm_source || params.utm_medium || params.utm_campaign);
    if (hasUTM) {
      const trim = v => (typeof v === 'string' ? v.trim() : '');
      const utmFields = {
        session_source: trim(params.utm_source) || 'NA',
        session_medium: trim(params.utm_medium) || 'NA',
        session_campaign_id: trim(params.utm_campaignID) || 'NA',
        session_campaign_name: trim(params.utm_campaign) || 'NA',
        session_campaign_ad_id: trim(params.utm_adID) || 'NA',
        session_campaign_ad_name: trim(params.utm_adcontent) || 'NA',
        session_campaign_adset_id: trim(params.utm_adsetID) || 'NA',
        session_campaign_adset_name: trim(params.utm_term) || 'NA',
        session_pid: trim(params.pid) || 'NA',
        session_offer_id: trim(params.offer_id) || 'NA',
        session_click_id: trim(params.clickid) || 'NA'
      };
      storeSessionUTM(utmFields);
      return utmFields;
    }
  }

  const stored = readSessionUTM();
  if (stored) return stored;

  return { ...NA_SESSION_FIELDS };
};

const initAmplitude = () => {
  if (isInitialized || !amplitudeApiKey || !isBrowser()) return;
  // eslint-disable-next-line no-underscore-dangle
  const isWebVitalEnabled = getEnableWebVitals(window?.__PRELOADED_STATE__);
  const trackingOptions = {
    sessions: true, // equivalent to +sessions
    pageViews: false, // you're tracking manually
    formInteractions: false,
    fileDownloads: false,
    attribution: false
  };
  amplitudeInit(amplitudeApiKey, {
    // Web Vitals only exists under `autocapture` (SDK 2.x), so the flag picks the
    // option key too: on → autocapture + webVitals; off → the pre-rollout
    // `defaultTracking` key with webVitals omitted entirely, so no
    // '[Amplitude] Web Vitals' events are sent.
    ...(isWebVitalEnabled
      ? {
          autocapture: {
            ...trackingOptions,
            webVitals: true // Core Web Vitals, sent as the '[Amplitude] Web Vitals' event
          }
        }
      : { defaultTracking: trackingOptions }),
    sessionTimeout: AMPLITUDE_SESSION_TIMEOUT, // new session after 30 min inactivity
    flushQueueSize: 1, // send each event immediately, one at a time
    flushIntervalMillis: 0, // no batching delay
    transport: 'beacon', // use sendBeacon so events survive page unload (e.g. window.location redirects after login)
    minIdLength: 1
  });
  captureSessionUTM(true);
  // Stamp the first-visit date on page load, so it is recorded even if the
  // visit produces no events.
  getFirstVisitDateProps();
  isInitialized = true;
};

if (isBrowser()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAmplitude();
    });
  } else {
    initAmplitude();
  }
}

export const amplitudeTrack = (eventName, eventProperties = {}, userId = '', deviceId = '', context = {} ) => {
  if (!isBrowser() || !amplitudeApiKey) return;
  initAmplitude();
  if (userId) setUserId(userId);

  const affiliateProps = getAffiliateProps();
  console.log(
    'Affiliate logs: [amplitude.js][amplitudeTrack] event:',
    eventName,
    'affiliateProps:',
    affiliateProps
  );
  const eventOptions = {
    ...(deviceId && { device_id: deviceId }),
    ...(context.userAgent && { user_agent: context.userAgent }),
    ...(context.locale && { language: context.locale }),
    ...(context.userAgentData?.platform && { platform: context.userAgentData.platform }),
    user_properties: {
      ...captureSessionUTM(),
      ...getSubscriptionAndSavingsProps(),
      ...getDeviceProps(),
      ...getReferralProps(),
      ...getCustomerProps(),
      ...getFirstVisitDateProps(),
      // ...getHashedUserProps(),
      ...affiliateProps
    }
  };

  const pageProps = context.page ?  {
    '[Amplitude] Page Domain': window?.location?.hostname,
    '[Amplitude] Page Location': window?.location?.href,
    '[Amplitude] Page Path': window?.location?.pathname,
    '[Amplitude] Page Title': document?.title,
    '[Amplitude] Page URL': window?.location?.href,
    '[Amplitude] Previous Page Location': document?.referrer,
    '[Amplitude] Previous Page Type': document?.referrer ? 'referral' : 'direct',
  } : {};

  amplitudeTrackEvent(
    eventName,
    {
      ...pageProps,
      ...eventProperties,
      session_id: getSessionId()
    },
    eventOptions
  );
};

export const amplitudeSetDeviceId = (deviceId = '') => {
  if (!isBrowser() || !amplitudeApiKey || !deviceId) return;
  initAmplitude();
  setDeviceId(deviceId);
};

export const amplitudeLogout = () => {
  try {
    if (!isBrowser() || !amplitudeApiKey) return;
    initAmplitude();
    setUserId(undefined);
    const deviceId = readCookie('segment_device_id') || '';
    if (deviceId) setDeviceId(deviceId);
  } catch (error) {
    console.log(error);
  }
};

export const amplitudeIdentify = (userId = '', traits = {}) => {
  if (!isBrowser() || !amplitudeApiKey) return;
  initAmplitude();
  if (userId) setUserId(userId);
  const affiliateProps = getAffiliateProps();
  console.log(
    'Affiliate logs: [amplitude.js][amplitudeIdentify] userId:',
    userId,
    'affiliateProps:',
    affiliateProps,
    'traits:',
    traits
  );
  amplitudeTrackEvent(
    'identify',
    {},
    {
      user_properties: {
        ...captureSessionUTM(),
        ...getSubscriptionAndSavingsProps(),
        ...getDeviceProps(),
        ...getReferralProps(),
        ...getFirstVisitDateProps(),
        ...affiliateProps,
        ...traits
      }
    }
  );
};

// Composed Amplitude user-properties snapshot — the exact same set of attributes
// that ride on every track event (see amplitudeTrack's `user_properties`). Exported
// so other flows (e.g. the SPC banner/microapp payload in Shipping.js) can embed an
// identical copy without duplicating the composition logic.
export const getAmplitudeUserProperties = () => ({
  ...captureSessionUTM(),
  ...getSubscriptionAndSavingsProps(),
  ...getDeviceProps(),
  ...getReferralProps(),
  ...getCustomerProps(),
  ...getFirstVisitDateProps(),
  ...getAffiliateProps()
});
 